import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { FlexColumn, FlexRow } from '../../../atoms/layout/Flex'
import useYup from '../../../../hooks/useYup'
import VisitorRegistrationWorkCategoriesForm from './WorkCategoriesForm'
import VisitorRegistrationQualificationsForm from './QualificationsForm'
import VisitorRegistrationSurveysForm from './SurveysForm'
import Button from '../../../atoms/inputs/Button'
import Form from '../../../atoms/inputs/Form'
import {
  VisitorRegistrationForm as VisitorRegistrationFormType,
  VisitorRegistrationAnswers,
  VisitorRegistrationAnswersFormValues,
} from '../../../../types/Company'
import { CompanyActions } from '../../../../store/company'
import { AppDispatch } from '../../../../store'

type Props = {
  form: VisitorRegistrationFormType
}

export default function VisitorRegistrationForm(props: Props) {
  const { form: propsForm } = props
  const yup = useYup()
  const form = useForm<VisitorRegistrationAnswersFormValues>({
    resolver: yupResolver(
      yup.object().shape({
        survey1: yup.number().required('必須項目です'),
        survey4: yup.number().required('必須項目です'),
        survey4File: yup.mixed().when(['survey4', 'survey4FileId'], {
          is: (survey4: number, survey4FileId: string | undefined) => survey4 === 1 && !survey4FileId,
          then: yup.mixed().required('加入証を添付してください'),
          otherwise: yup.mixed().notRequired(),
        }),
        survey5: yup.number().required('必須項目です'),
        survey6: yup.number().required('必須項目です'),
        survey6Name: yup.string().when('survey6', {
          is: 1,
          then: (schema) => schema.required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        survey7: yup.number().required('必須項目です'),
        survey7Name: yup.string().when('survey7', {
          is: 1,
          then: (schema) => schema.required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        survey8: yup.number().required('必須項目です'),
        survey9: yup.number().required('必須項目です'),
        survey9File: yup.mixed().when(['survey9', 'survey9FileId'], {
          is: (survey9: number, survey9FileId: string | undefined) => survey9 === 1 && !survey9FileId,
          then: yup.mixed().required('加入証を添付してください'),
          otherwise: yup.mixed().notRequired(),
        }),
        survey10: yup.number().nullable().required('必須項目です'),
        survey12: yup.number().required('必須項目です'),
        survey13: yup.number().required('必須項目です'),
        survey14: yup.number().required('必須項目です'),
        survey15: yup.number().required('必須項目です'),
        survey16: yup.number().required('必須項目です'),
        survey16File: yup.mixed().when(['survey16', 'survey16FileId'], {
          is: (survey16: number, survey16FileId: string | undefined) => survey16 === 1 && !survey16FileId,
          then: yup.mixed().required('加入証を添付してください'),
          otherwise: yup.mixed().notRequired(),
        }),
        survey17: yup.number().required('必須項目です'),
        survey18: yup.number().max(9999).nullable().required('必須項目です'),
        workCategoryOtherText: yup.string().when('workCategoryOther', {
          is: true,
          then: yup.string().required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        qualificationAOtherText: yup.string().when('qualificationAOther', {
          is: true,
          then: yup.string().required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        qualificationBOtherText: yup.string().when('qualificationBOther', {
          is: true,
          then: yup.string().required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        qualificationCOtherText: yup.string().when('qualificationCOther', {
          is: true,
          then: yup.string().required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
        qualificationDOtherText: yup.string().when('qualificationDOther', {
          is: true,
          then: yup.string().required('必須項目です'),
          otherwise: yup.string().notRequired(),
        }),
      })
    ),
    defaultValues: propsForm.answers ?? undefined,
  })
  const dispatch = useDispatch<AppDispatch>()

  const handleSave = useCallback(
    async (params: VisitorRegistrationAnswersFormValues) => {
      const { survey4File, survey9File, survey16File, ...answers } = params

      // ファイルを先にアップロードしてIDとファイル名を取得
      const uploadPromises: Promise<{ answerId: string; fileId?: string; fileName?: string }>[] = []

      if (survey4File) {
        uploadPromises.push(
          dispatch(CompanyActions.uploadVisitorRegistrationFile({ file: survey4File, answerId: 'survey4' })).then(
            (result) => ({
              answerId: 'survey4',
              fileId: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileId
                : undefined,
              fileName: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileName
                : undefined,
            })
          )
        )
      }
      if (survey9File) {
        uploadPromises.push(
          dispatch(CompanyActions.uploadVisitorRegistrationFile({ file: survey9File, answerId: 'survey9' })).then(
            (result) => ({
              answerId: 'survey9',
              fileId: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileId
                : undefined,
              fileName: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileName
                : undefined,
            })
          )
        )
      }
      if (survey16File) {
        uploadPromises.push(
          dispatch(CompanyActions.uploadVisitorRegistrationFile({ file: survey16File, answerId: 'survey16' })).then(
            (result) => ({
              answerId: 'survey16',
              fileId: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileId
                : undefined,
              fileName: CompanyActions.uploadVisitorRegistrationFile.fulfilled.match(result)
                ? result.payload.fileName
                : undefined,
            })
          )
        )
      }

      // アップロード完了を待つ
      const uploadResults = await Promise.all(uploadPromises)

      // アップロード結果からFileIdとFileNameを設定
      const answersWithFileIds: VisitorRegistrationAnswers = { ...answers }
      uploadResults.forEach(({ answerId, fileId, fileName }) => {
        if (answerId === 'survey4') {
          if (fileId) answersWithFileIds.survey4FileId = fileId
          if (fileName) answersWithFileIds.survey4FileName = fileName
        }
        if (answerId === 'survey9') {
          if (fileId) answersWithFileIds.survey9FileId = fileId
          if (fileName) answersWithFileIds.survey9FileName = fileName
        }
        if (answerId === 'survey16') {
          if (fileId) answersWithFileIds.survey16FileId = fileId
          if (fileName) answersWithFileIds.survey16FileName = fileName
        }
      })

      // 未加入の場合はFileIdとFileNameをクリア
      if (answersWithFileIds.survey4 === 2) {
        answersWithFileIds.survey4FileId = undefined
        answersWithFileIds.survey4FileName = undefined
      }
      if (answersWithFileIds.survey9 === 2) {
        answersWithFileIds.survey9FileId = undefined
        answersWithFileIds.survey9FileName = undefined
      }
      if (answersWithFileIds.survey16 === 2) {
        answersWithFileIds.survey16FileId = undefined
        answersWithFileIds.survey16FileName = undefined
      }

      // 回答を保存
      const saveResult = await dispatch(CompanyActions.saveVisitorRegistrationAnswers({ answers: answersWithFileIds }))

      // 保存成功後、フォームの値を更新してファイルアイコン表示に切り替え
      if (CompanyActions.saveVisitorRegistrationAnswers.fulfilled.match(saveResult)) {
        // Fileオブジェクトをクリア
        form.setValue('survey4File', undefined)
        form.setValue('survey9File', undefined)
        form.setValue('survey16File', undefined)

        // FileIdとFileNameを設定
        form.setValue('survey4FileId', answersWithFileIds.survey4FileId)
        form.setValue('survey4FileName', answersWithFileIds.survey4FileName)
        form.setValue('survey9FileId', answersWithFileIds.survey9FileId)
        form.setValue('survey9FileName', answersWithFileIds.survey9FileName)
        form.setValue('survey16FileId', answersWithFileIds.survey16FileId)
        form.setValue('survey16FileName', answersWithFileIds.survey16FileName)
      }
    },
    [dispatch, form]
  )

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(handleSave)}>
        <FlexColumn>
          <VisitorRegistrationSurveysForm />
          <VisitorRegistrationWorkCategoriesForm />
          <VisitorRegistrationQualificationsForm />
          <FlexRow>
            <Button type="submit">保存</Button>
          </FlexRow>
        </FlexColumn>
      </Form>
    </FormProvider>
  )
}
